import { parseVariableRef } from '../../../../utils/variableScope';
import { joinPathLineBreaks } from '../../../../utils/variableScope/resolve';
import { refreshMathHtml } from './mathPills';
import { pillRefFromTag } from './pillHtml';

/**
 * Pill text is stored in the block HTML and laid out inline. Past this, show
 * a middle-truncated preview — the full value stays on the variable itself.
 */
/** Inline pills are laid out in the paragraph. Keep the preview short. */
export const PILL_DISPLAY_MAX = 160;

/**
 * Truncate long preview strings in the middle so start and end stay visible.
 * e.g. "D:/very/long/…/file.wav"
 */
export const truncateMiddle = (text, max = 40) => {
  const s = String(text ?? '');
  if (s.length <= max) return s;
  if (max < 3) return s.slice(0, max);
  const inner = max - 1; // room for …
  const head = Math.ceil(inner / 2);
  const tail = Math.floor(inner / 2);
  return `${s.slice(0, head)}…${s.slice(s.length - tail)}`;
};

/** Cursor-following tip label: `{{name}}` / `{{name[1]}}` / `{{name[*]}}`. */
export const variableTipLabel = (ref, hit) => {
  const braced = `{{${ref}}}`;
  if (!hit) return `${braced} - No value in this context.`;
  const from = hit.sourceName ? `From ${hit.sourceName}.` : 'From unknown source.';
  if (hit.isArray && hit.index == null) {
    const n = Array.isArray(hit.values) ? hit.values.length : 0;
    const base = parseVariableRef(ref).name || ref;
    return `${braced} - ${from} (array · ${base}[1]…${base}[${Math.max(n, 1)}] or ${base}[*])`;
  }
  if (hit.all) {
    const lines = String(hit.value ?? '')
      .split('\n')
      .filter((l) => l.trim());
    return `${braced} - ${from} (${lines.length} value${lines.length === 1 ? '' : 's'}, one per line)`;
  }
  const value = String(hit.value ?? '');
  if (!value.trim()) return `${braced} - ${from} (empty)`;
  return `${braced} - ${from}`;
};

const pillDisplay = (ref, hit) => {
  const tip = variableTipLabel(ref, hit);
  if (!hit) {
    return {
      state: 'unresolved',
      text: ref,
      color: '',
      tip,
    };
  }
  const value = hit.all
    ? String(hit.value ?? '')
    : joinPathLineBreaks(String(hit.value ?? ''), { joinInternal: true });
  if (!value.trim()) {
    return {
      state: 'empty',
      text: ref,
      color: hit.color || '',
      tip,
    };
  }
  // Keep real newlines for {{name[*]}} — CSS pre-line + code copy rely on them.
  // Cap what is written into the block. A multi-megabyte value here is laid
  // out inline (overflow-wrap) and serialized into every undo snapshot.
  const text =
    value.length > PILL_DISPLAY_MAX ? truncateMiddle(value, PILL_DISPLAY_MAX) : value;
  return { state: 'resolved', text, color: hit.color || '', tip };
};

/**
 * Rewrite every variable pill in a block's HTML to show the CURRENT
 * context's resolution. `resolveVariable(ref)` accepts `name` or `name[1]`
 * and returns { value, color, sourceName } or null. Returns the original
 * string when nothing changed so callers can cheap-compare.
 */
export const refreshVariableHtml = (html, resolveVariable) => {
  if (!html) return html;
  const hasVars = html.includes('data-doc-variable');
  const hasMath = html.includes('data-doc-math');
  if (!hasVars && !hasMath) return html;

  let next = html;
  if (hasMath) {
    const mathNext = refreshMathHtml(next, resolveVariable);
    if (mathNext !== next) next = mathNext;
  }
  if (!next.includes('data-doc-variable')) return next;

  const host = document.createElement('div');
  host.innerHTML = next;
  let changed = false;

  host.querySelectorAll('[data-doc-variable]').forEach((tag) => {
    const ref = pillRefFromTag(tag);
    const { state, text, color, tip } = pillDisplay(
      ref,
      resolveVariable ? resolveVariable(ref) : null
    );

    if (tag.getAttribute('data-var-state') !== state) {
      tag.setAttribute('data-var-state', state);
      changed = true;
    }
    if (tag.textContent !== text) {
      tag.textContent = text;
      changed = true;
    }
    const style = color ? `--var-color: ${color};` : null;
    if ((tag.getAttribute('style') || null) !== style) {
      if (style) tag.setAttribute('style', style);
      else tag.removeAttribute('style');
      changed = true;
    }
    if ((tag.getAttribute('data-var-tip') || '') !== tip) {
      tag.setAttribute('data-var-tip', tip);
      changed = true;
    }
    // Drop legacy native tooltips — the cursor-following tip replaces them
    if (tag.hasAttribute('title')) {
      tag.removeAttribute('title');
      changed = true;
    }
    if (tag.getAttribute('spellcheck') !== 'false') {
      tag.setAttribute('spellcheck', 'false');
      changed = true;
    }
  });

  return changed ? host.innerHTML : next;
};
